import React, { useCallback, useState } from 'react'
import { Box, Button, Card, Grid, Typography } from '@mui/material'
import CircularLoading from '../../../../../../../components/CircularProgress'
import {
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
  OrderShippingProvider,
  OrderSummary,
} from '../../../../../../../types/order'
import EditIcon from '@mui/icons-material/Edit'
import { message, Table, TableColumnsType, Tag } from 'antd'
import dayjs from 'dayjs'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { ILead } from '../../../../../../../types/lead'
import LeadOrderDrawer from './LeadOrderDrawer'
import useAxios from '../../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import TrackingProviderImage from '../../../../../../../components/TrackingProviderImage'
import OrderTag from '../../../../../../../components/OrderTag'
import PageUrls from '../../../../../../../constants/page-urls'
import { Link, useNavigate } from 'react-router-dom'
import ProductItemDetailTag from '../../../../../../../components/ProductItemDetailTag'
import { useUser } from '../../../../../../../hooks/useUser'

interface FilterGroupProps {
  isLoading: boolean
  data?: ILead
  onUpdateLeadSuccess: () => void
  isFinished?: boolean
}

const LeadOrderHistoryDetailCard = ({ isLoading, data, isFinished }: FilterGroupProps) => {
  const [isOpenedOrderDrawer, setIsOpenedOrderDrawer] = useState<boolean>(false)

  const { user } = useUser()

  const ordersList = useAxios<
    PaginationResponseInterface<IOrder> & { total: OrderSummary }
  >({
    method: 'GET',
    url: '/order',
    params: {
      page: 1,
      perPage: 10,
      // recipient: data?.customer?.name,
      recipientPhone: data?.customer?.phone,
      method: 'lead',
    },
  })

  const onUpdateOrdersListSuccess = useCallback(async () => {
    try {
      await ordersList.mutate()
    } catch (error) {
      message.error('ไม่สามารถดึงข้อมูลรายการสั่งซื้อได้')
    }
  }, [ordersList])

  const columns: TableColumnsType<IOrder & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      align: 'center' as const,
      render: (key: number) => {
        return (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign={'center'}
          >
            {key + 1}
          </Typography>
        )
      },
    },
    {
      title: 'เลขออเดอร์',
      dataIndex: 'code',
      key: 'code',
      align: 'center' as const,
      render: (code: string, order: IOrder) => {
        if (user.username !== order.actor && user.roles?.includes('user')) {
          return (
            <>
              <Typography variant="subtitle1" color="text.primary">
                {code}
              </Typography>
              <Typography fontSize={11}>
                {order.orderDate
                  ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                  : ''}
              </Typography>
            </>
          )
        }
        return (
          <>
            <Typography
              variant="subtitle1"
              color="text.primary"
              component={Link}
              to={`${PageUrls.Orders.Detail}/${order.objectId}`}
              sx={{ cursor: 'pointer' }}
            >
              {code}
            </Typography>
            <Typography fontSize={11}>
                {order.orderDate
                  ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                  : ''}
              </Typography>
          </>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string, order: IOrder) => {
        return (
          <>
            {order.trackingCode && order.trackingProvider && (
              <Box mb={1}>
                <TrackingProviderImage
                  trackingProvider={
                    order.trackingProvider as OrderShippingProvider
                  }
                />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  textAlign={'center'}
                >
                  {order.trackingCode} {order.providerStatus}
                </Typography>
              </Box>
            )}
            <Box>
              <OrderTag order={order} status={status as EOrderStatus} />
            </Box>
            {order.providerStatusText && (
              <Box pt={1}>
                <Tag color={`success`}>{order.providerStatusText}</Tag>
              </Box>
            )}
          </>
        )
      },
    },
    {
      title: 'สินค้า',
      dataIndex: 'items',
      key: 'items',
      align: 'center' as const,
      render: (items: IOrderItem[]) => (
        <Box minWidth={150}>
          {items?.map((item: IOrderItem, index: number) => {
            return (
              <ProductItemDetailTag
                key={index}
                item={{
                  name: item.productName,
                  quantity: item.quantity,
                  url: item.imageUrl,
                  isCollection: item.isCollection,
                  commissionName: item.commissionUsername,
                  color: item.meta?.color,
                  size: item.meta?.size,
                }}
              />
            )
          })}
        </Box>
      ),
    },
    {
      title: 'ราคา',
      dataIndex: 'total',
      key: 'total',
      align: 'center' as const,
      render: (total: string, order: IOrder) => (
        <Grid
          container
          flexDirection={'column'}
          textAlign={'center'}
          minWidth={120}
        >
          <Grid item>
            <Typography variant="h5" color="error.main">
              {total.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ marginTop: 1 }}
            >
              {order?.paymentMethod === EOrderType.COD
                ? 'เก็บเงินปลายทาง'
                : 'โอนผ่านธนาคาร'}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {order.items.length} รายการ/{order.quantity} ชิ้น
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      title: 'วันที่',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center' as const,
      render: (createdAt: string) => {
        return (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)} น.
              </Typography>
            </Grid>
          </Grid>
        )
      },
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'isCrm',
      key: 'order',
      width: 120,
      align: 'left' as const,
      render: (isCrm: boolean, order: IOrder) => {
        return (
          <Grid
            container
            flexDirection="column"
            alignItems="start"
            spacing={0.5}
          >
            {order.note && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  หมายเหตุ: {order.note}
                </Typography>
              </Grid>
            )}
            {order.recipient.social && (
              <Grid item>
                <Typography variant="caption" color="text.secondary">
                  โซเชียล: {order.recipient.social}
                </Typography>
              </Grid>
            )}
            {isCrm && (
              <Grid item>
                <Tag color="warning" style={{ margin: 0 }}>
                  <Typography variant="subtitle2">CRM</Typography>
                </Tag>
              </Grid>
            )}
          </Grid>
        )
      },
    },
  ]

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <LeadOrderDrawer
        isOpen={isOpenedOrderDrawer}
        onClose={() => setIsOpenedOrderDrawer(false)}
        onUpdateOrdersListSuccess={onUpdateOrdersListSuccess}
        lead={data}
      />
      <Card sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            lg={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              ประวัติการสั่งซื้อ
            </Typography>
            {data?.cancelAt === null && !isFinished && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => setIsOpenedOrderDrawer(true)}
              >
                สร้างคำสั่งซื้อใหม่
              </Button>
            )}
          </Grid>
          <Grid item xs={12} lg={12}>
            <Table
              size={'middle'}
              columns={columns}
              scroll={{
                x: columns.reduce(
                  (pre, cur: any) => (cur.width ? cur.width + pre : pre),
                  0,
                ),
              }}
              dataSource={ordersList.data?.records?.map(
                (order: IOrder, index: number) => {
                  return {
                    ...order,
                    key: index,
                  }
                },
              )}
              bordered
              pagination={false}
            />
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

export default LeadOrderHistoryDetailCard
